import * as React from 'react';
import { useState, useCallback } from 'react';

import { Col, Row, Table } from 'components/bootstrap';
import PaginatedList from 'components/common/PaginatedList';
import SearchForm from 'components/common/SearchForm';
import Spinner from 'components/common/Spinner';
import useUserSearchFiltersQuery from 'search-filter/hooks/useUserSearchFiltersQuery';
import QueryHelper from 'components/common/QueryHelper';
import { FILTER_SCOPES } from 'search-filter/constants';
import usePaginationQueryParameter from 'hooks/usePaginationQueryParameter';
import type { UserSearchFilter } from 'search-filter/types';
import usePluggableEntityTableElements from 'hooks/usePluggableEntityTableElements';
import { NoEntitiesExist } from 'components/common';

import FiltersOverviewItem from './FiltersOverviewItem';

const FiltersOverview = () => {
  const { page, pageSize, resetPage } = usePaginationQueryParameter();
  const [filterQuery, setSearchFilter] = useState('');
  const { pluggableTableHeaders } = usePluggableEntityTableElements<UserSearchFilter & { id: string }>(
    null,
    'search_filter',
  );

  const {
    data: { filters = [], pagination },
    isFetching,
  } = useUserSearchFiltersQuery({ query: filterQuery, page, per_page: pageSize, scope: FILTER_SCOPES.ALL });
  const handleSearch = useCallback(
    (newSearchQuery: string | undefined) => {
      setSearchFilter(newSearchQuery);
      resetPage();
    },
    [resetPage, setSearchFilter],
  );
  const handleReset = useCallback(() => {
    handleSearch(undefined);
  }, [handleSearch]);
  const ListHeader = useCallback(
    () => (
      <thead>
        <tr>
          <th>Title</th>
          <th>Query String</th>
          <th>Description</th>
          {pluggableTableHeaders}
          <th className="text-right">Actions</th>
        </tr>
      </thead>
    ),
    [pluggableTableHeaders],
  );

  return (
    <Row className="content">
      <Col xs={12}>
        <PaginatedList totalItems={pagination?.total ?? 0}>
          <SearchForm
            onSearch={handleSearch}
            onReset={handleReset}
            topMargin={0}
            queryHelpComponent={
              <QueryHelper
                entityName="filter"
                commonFields={['id', 'title', 'description']}
                fieldMap={{ queryString: 'Query string of the filter' }}
              />
            }
          />
          <Table condensed hover striped>
            <ListHeader />
            <tbody>
              {isFetching && (
                <tr>
                  <td colSpan={4} aria-label="Loading Filters">
                    <Spinner delay={0} />
                  </td>
                </tr>
              )}
              {filters.length < 1 ? (
                <tr>
                  <td colSpan={4} aria-label="No search filter found">
                    <NoEntitiesExist>No search filter found.</NoEntitiesExist>
                  </td>
                </tr>
              ) : (
                <>
                  {filters.map((filter) => (
                    <FiltersOverviewItem key={filter.id} filter={filter} />
                  ))}
                </>
              )}
            </tbody>
          </Table>
        </PaginatedList>
      </Col>
    </Row>
  );
};

export default FiltersOverview;
